/**
 * Abort signals with a deadline.
 *
 * SPEC §13 makes this a hard rule: **every** request to an external link carries
 * a timeout. A `fetch` with no signal has no ceiling at all — Safari will hold a
 * connection to a black-holed host for minutes — and with four probe slots, a
 * handful of those stall the entire cycle.
 */

/**
 * A signal that aborts after `ms`, plus the cleanup that cancels the timer.
 *
 * `AbortSignal.timeout` (Safari 16+) is the whole implementation on any device
 * that has it. The `AbortController` fallback exists because this module sits
 * under `probeReachable`, which is required to be total: a `TypeError` thrown
 * here on an older iOS would escape the probe's own `try` and take the run down,
 * which is a far worse failure than the missing feature.
 */
export function timeoutSignal(ms: number): { signal: AbortSignal; cancel: () => void } {
  const native = (AbortSignal as { timeout?: (ms: number) => AbortSignal }).timeout;
  if (typeof native === 'function') {
    return { signal: native.call(AbortSignal, ms), cancel: () => {} };
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException('Timeout', 'TimeoutError')), ms);
  return { signal: controller.signal, cancel: () => clearTimeout(timer) };
}

/** `setTimeout` as a promise. Used for the gap between rounds. */
export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(finish, ms);
    function finish() {
      clearTimeout(timer);
      signal?.removeEventListener('abort', finish);
      resolve();
    }
    // Resolves rather than rejects on abort: every caller's next move is to
    // check the deadline itself, and a rejection here would only be caught and
    // discarded one frame later.
    signal?.addEventListener('abort', finish, { once: true });
  });
}
